import { useId, useState, type FormEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Banner,
  Button,
  Checkbox,
  Combobox,
  Field,
  IconButton,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ICON_SIZE,
  ICON_STROKE,
  compareNames,
  type ComboboxOption,
} from "@optymyse/suite-ui";
import type { Contact } from "../../../shared/types/directory";
import type { CompanyLinkBody, ContactBody, ContactChangeBody, MethodBody } from "../../lib/directory";
import { filled } from "../../lib/format";
import { strings } from "../../lib/strings";
import type { FormProblem } from "./CompanyForm";

type Kind = "email" | "phone" | "other";

/** One way to reach them, as typed. Link renews each one's id at every save, so the form keeps none. */
type MethodDraft = { kind: Kind; value: string; label: string; is_primary: boolean };

/** A link to a customer, as typed. */
type LinkDraft = {
  company_id: string;
  relationship: string;
  title: string;
  department: string;
  is_primary: boolean;
};

export type ContactDraft = {
  display_name: string;
  given_name: string;
  family_name: string;
  preferred_name: string;
  methods: MethodDraft[];
  companies: LinkDraft[];
};

/** A customer the form can link to: one of the directory's, or one the contact is linked to already. */
export type CompanyChoice = { readonly id: string; readonly name: string; readonly detail: string | null };

/** The form's start: the contact as read, or, for a new one, nothing but the customer it's being added to. */
export function draftOf(contact?: Contact, companyId?: string): ContactDraft {
  if (!contact)
    return {
      display_name: "",
      given_name: "",
      family_name: "",
      preferred_name: "",
      methods: [{ kind: "email", value: "", label: "", is_primary: false }],
      companies: companyId
        ? [{ company_id: companyId, relationship: "", title: "", department: "", is_primary: false }]
        : [],
    };
  return {
    display_name: contact.display_name,
    given_name: contact.given_name ?? "",
    family_name: contact.family_name ?? "",
    preferred_name: contact.preferred_name ?? "",
    methods: contact.methods.map((method) => ({
      kind: method.kind,
      value: method.value,
      label: method.label ?? "",
      is_primary: method.is_primary,
    })),
    companies: contact.companies.map((link) => ({
      company_id: link.company_id,
      relationship: link.relationship,
      title: link.title ?? "",
      department: link.department ?? "",
      is_primary: link.is_primary,
    })),
  };
}

function methodsOf(draft: ContactDraft): MethodBody[] {
  return draft.methods.flatMap((method): MethodBody[] => {
    const value = filled(method.value);
    if (value === null) return [];
    const label = filled(method.label);
    const primary = method.is_primary ? { is_primary: true } : {};
    if (method.kind === "other") return [{ kind: "other", value, label: label ?? "", ...primary }];
    return [{ kind: method.kind, value, ...(label === null ? {} : { label }), ...primary }];
  });
}

function companiesOf(draft: ContactDraft): CompanyLinkBody[] {
  return draft.companies.flatMap((link): CompanyLinkBody[] => {
    if (link.company_id === "") return [];
    const relationship = filled(link.relationship);
    const title = filled(link.title);
    const department = filled(link.department);
    return [
      {
        company_id: link.company_id,
        ...(relationship === null ? {} : { relationship }),
        ...(title === null ? {} : { title }),
        ...(department === null ? {} : { department }),
        ...(link.is_primary ? { is_primary: true } : {}),
      },
    ];
  });
}

/** A new contact from what was typed. */
export function bodyOf(draft: ContactDraft): ContactBody {
  const names = {
    display_name: filled(draft.display_name),
    given_name: filled(draft.given_name),
    family_name: filled(draft.family_name),
    preferred_name: filled(draft.preferred_name),
  };
  return {
    ...Object.fromEntries(Object.entries(names).filter(([, value]) => value !== null)),
    methods: methodsOf(draft),
    companies: companiesOf(draft),
  };
}

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What changed from the contact as read. Their names go together when any changes, the display name with them, empty
 * to have Link make it from their names (PRODUCT_API.md, "Contacts"). Their ways to reach them, and their customers,
 * are each sent whole when any part changed, as Link replaces each list whole.
 */
export function changeOf(contact: Contact, draft: ContactDraft): ContactChangeBody {
  const change: ContactChangeBody = {};
  const names = {
    display_name: filled(draft.display_name),
    given_name: filled(draft.given_name),
    family_name: filled(draft.family_name),
    preferred_name: filled(draft.preferred_name),
  };
  if (
    names.display_name !== contact.display_name ||
    names.given_name !== contact.given_name ||
    names.family_name !== contact.family_name ||
    names.preferred_name !== contact.preferred_name
  )
    Object.assign(change, names);
  const methods = methodsOf(draft);
  const read = contact.methods.map((method) => [method.kind, method.value, method.label, method.is_primary]);
  if (
    !sameJson(
      methods.map((method) => [method.kind, method.value, method.label ?? null, method.is_primary ?? false]),
      read,
    )
  )
    change.methods = methods;
  const companies = companiesOf(draft);
  const linked = contact.companies.map((link) => [
    link.company_id,
    link.relationship,
    link.title,
    link.department,
    link.is_primary,
  ]);
  const typed = companies.map((link) => [
    link.company_id,
    link.relationship ?? "employee",
    link.title ?? null,
    link.department ?? null,
    link.is_primary ?? false,
  ]);
  if (!sameJson(typed, linked)) change.companies = companies;
  return change;
}

const KINDS: readonly Kind[] = ["email", "phone", "other"];

/**
 * A contact's fields, for a new one or a change: their names, the ways to reach them (email addresses, phone numbers
 * and others, each with a label, one primary of each kind), and the customers they're linked to (with what they are
 * there, one primary). What it sends is the page's to say (`onSubmit`).
 */
export function ContactForm({
  initial,
  companies,
  submitLabel,
  pending,
  problem,
  onSubmit,
  onCancel,
}: {
  initial: ContactDraft;
  companies: readonly CompanyChoice[];
  submitLabel: string;
  pending: boolean;
  problem: FormProblem | null;
  onSubmit: (draft: ContactDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [missing, setMissing] = useState<"name" | "label" | null>(null);
  const ids = useId();
  const options: ComboboxOption[] = [...companies]
    .sort((a, b) => compareNames(a.name, b.name))
    .map((company) => ({
      value: company.id,
      label: company.name,
      ...(company.detail ? { keywords: [company.detail], hint: company.detail } : {}),
    }));
  const fieldProblem = (field: string) => (problem?.field === field ? problem.message : undefined);
  const setName = (field: "display_name" | "given_name" | "family_name" | "preferred_name") => (value: string) =>
    setDraft((was) => ({ ...was, [field]: value }));
  const setMethod = (index: number, method: Partial<MethodDraft>) =>
    setDraft((was) => ({
      ...was,
      methods: was.methods.map((each, at) => {
        if (at === index) return { ...each, ...method };
        // One primary of each kind: choosing one takes it from the others of its kind.
        const kind = method.kind ?? was.methods[index]!.kind;
        return method.is_primary && each.kind === kind ? { ...each, is_primary: false } : each;
      }),
    }));
  const setLink = (index: number, link: Partial<LinkDraft>) =>
    setDraft((was) => ({
      ...was,
      companies: was.companies.map((each, at) =>
        at === index ? { ...each, ...link } : link.is_primary ? { ...each, is_primary: false } : each,
      ),
    }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!filled(draft.display_name) && !filled(draft.given_name) && !filled(draft.family_name)) {
      setMissing("name");
      return;
    }
    if (draft.methods.some((method) => method.kind === "other" && filled(method.value) && !filled(method.label))) {
      setMissing("label");
      return;
    }
    setMissing(null);
    onSubmit(draft);
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-6">
      {problem !== null && problem.field === null && <Banner tone="negative">{problem.message}</Banner>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={strings.contacts.form.displayName}
          hint={strings.contacts.form.displayNameHint}
          error={missing === "name" ? strings.contacts.form.required : fieldProblem("display_name")}
          className="sm:col-span-2"
        >
          <Input
            value={draft.display_name}
            maxLength={240}
            autoComplete="off"
            onChange={(event) => setName("display_name")(event.target.value)}
          />
        </Field>
        <Field label={strings.contacts.form.givenName}>
          <Input
            value={draft.given_name}
            maxLength={200}
            autoComplete="off"
            onChange={(event) => setName("given_name")(event.target.value)}
          />
        </Field>
        <Field label={strings.contacts.form.familyName}>
          <Input
            value={draft.family_name}
            maxLength={200}
            autoComplete="off"
            onChange={(event) => setName("family_name")(event.target.value)}
          />
        </Field>
        <Field label={strings.contacts.form.preferredName}>
          <Input
            value={draft.preferred_name}
            maxLength={200}
            autoComplete="off"
            onChange={(event) => setName("preferred_name")(event.target.value)}
          />
        </Field>
      </div>

      <fieldset aria-labelledby={`${ids}-methods`} className="flex flex-col gap-3">
        <div>
          <h2 id={`${ids}-methods`} className="text-heading">
            {strings.contacts.form.methods}
          </h2>
          <p className="text-secondary text-ink-muted">{strings.contacts.form.methodsHint}</p>
        </div>
        {fieldProblem("methods") && <Banner tone="negative">{fieldProblem("methods")}</Banner>}
        {draft.methods.map((method, index) => (
          <div
            key={index}
            role="group"
            aria-label={`${strings.contacts.form.methods} ${index + 1}`}
            className="grid items-end gap-3 rounded-card border border-line p-3 sm:grid-cols-[8rem_minmax(0,1fr)_10rem_auto_auto]"
          >
            <Field label={strings.contacts.form.kind}>
              <Select value={method.kind} onValueChange={(kind) => setMethod(index, { kind: kind as Kind })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {strings.contacts.kinds[kind]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={strings.contacts.form.value}>
              <Input
                type={method.kind === "email" ? "email" : method.kind === "phone" ? "tel" : "text"}
                value={method.value}
                maxLength={method.kind === "email" ? 320 : method.kind === "phone" ? 80 : 500}
                autoComplete="off"
                onChange={(event) => setMethod(index, { value: event.target.value })}
              />
            </Field>
            <Field
              label={strings.contacts.form.label}
              error={
                missing === "label" && method.kind === "other" && !filled(method.label)
                  ? strings.contacts.form.labelRequired
                  : undefined
              }
            >
              <Input
                value={method.label}
                maxLength={200}
                autoComplete="off"
                onChange={(event) => setMethod(index, { label: event.target.value })}
              />
            </Field>
            <Field label={strings.contacts.form.primary} layout="inline" className="h-[38px] items-center">
              <Checkbox
                checked={method.is_primary}
                onCheckedChange={(checked) => setMethod(index, { is_primary: checked === true })}
              />
            </Field>
            <IconButton
              label={`${strings.contacts.form.removeMethod} ${method.value || strings.contacts.kinds[method.kind]}`}
              onClick={() => setDraft((was) => ({ ...was, methods: was.methods.filter((_, at) => at !== index) }))}
            >
              <Trash2 aria-hidden size={ICON_SIZE} strokeWidth={ICON_STROKE} />
            </IconButton>
          </div>
        ))}
        {draft.methods.length < 20 && (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                setDraft((was) => ({
                  ...was,
                  methods: [...was.methods, { kind: "email", value: "", label: "", is_primary: false }],
                }))
              }
            >
              <Plus aria-hidden strokeWidth={ICON_STROKE} />
              {strings.contacts.form.addMethod}
            </Button>
          </div>
        )}
      </fieldset>

      <fieldset aria-labelledby={`${ids}-companies`} className="flex flex-col gap-3">
        <h2 id={`${ids}-companies`} className="text-heading">
          {strings.contacts.form.companies}
        </h2>
        {fieldProblem("companies") && <Banner tone="negative">{fieldProblem("companies")}</Banner>}
        {draft.companies.map((link, index) => (
          <div
            key={index}
            role="group"
            aria-label={`${strings.contacts.form.company} ${index + 1}`}
            className="grid items-end gap-3 rounded-card border border-line p-3 sm:grid-cols-2"
          >
            <Field label={strings.contacts.form.company} className="sm:col-span-2">
              <Combobox
                variant="field"
                value={link.company_id === "" ? null : link.company_id}
                onValueChange={(companyId) => setLink(index, { company_id: companyId })}
                options={options}
                placeholder={strings.contacts.form.chooseCompany}
                searchLabel={strings.contacts.form.findCompany}
              />
            </Field>
            <Field label={strings.contacts.form.relationship} hint={strings.contacts.form.relationshipHint}>
              <Input
                value={link.relationship}
                maxLength={80}
                autoComplete="off"
                onChange={(event) => setLink(index, { relationship: event.target.value })}
              />
            </Field>
            <Field label={strings.contacts.form.title}>
              <Input
                value={link.title}
                maxLength={200}
                autoComplete="off"
                onChange={(event) => setLink(index, { title: event.target.value })}
              />
            </Field>
            <Field label={strings.contacts.form.department}>
              <Input
                value={link.department}
                maxLength={200}
                autoComplete="off"
                onChange={(event) => setLink(index, { department: event.target.value })}
              />
            </Field>
            <div className="flex items-center justify-between gap-3">
              <Field label={strings.contacts.form.primary} layout="inline">
                <Checkbox
                  checked={link.is_primary}
                  onCheckedChange={(checked) => setLink(index, { is_primary: checked === true })}
                />
              </Field>
              <IconButton
                label={`${strings.contacts.form.removeCompany} ${companies.find((company) => company.id === link.company_id)?.name ?? strings.contacts.form.company}`}
                onClick={() =>
                  setDraft((was) => ({ ...was, companies: was.companies.filter((_, at) => at !== index) }))
                }
              >
                <Trash2 aria-hidden size={ICON_SIZE} strokeWidth={ICON_STROKE} />
              </IconButton>
            </div>
          </div>
        ))}
        {draft.companies.length < 20 && (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                setDraft((was) => ({
                  ...was,
                  companies: [
                    ...was.companies,
                    { company_id: "", relationship: "", title: "", department: "", is_primary: false },
                  ],
                }))
              }
            >
              <Plus aria-hidden strokeWidth={ICON_STROKE} />
              {strings.contacts.form.addCompany}
            </Button>
          </div>
        )}
      </fieldset>

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          {strings.contacts.form.cancel}
        </Button>
        <Button type="submit" variant="primary" pending={pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
